import React, { useState, useRef, PropsWithChildren } from "react";

import ReactDOM from "react-dom";
import MobileTooltip from "common/MobileTooltip/MobileTooltip";
import { useStore } from "store";

import { cn } from "helpers/style";
import { useBreakpoint, useTooltipComputedStyles } from "helpers/hooks";
import styles from "./Tooltip.module.scss";

export type PlacementType =
  | "bottom-end"
  | "bottom-start"
  | "bottom"
  | "left-end"
  | "left-start"
  | "left"
  | "right-end"
  | "right-start"
  | "right"
  | "top-end"
  | "top-start"
  | "top";

export type TooltipProps = {
  placement: PlacementType;
  width?: number;
  title: string | JSX.Element;
  maxWidth?: number;
  withoutOKButton?: boolean;
  labelClassName?: string;
  onTooltipShow?: () => void;
};

const Tooltip: React.FC<PropsWithChildren<TooltipProps>> = ({
  placement = "top",
  width,
  title,
  children,
  maxWidth = 200,
  withoutOKButton,
  labelClassName,
  onTooltipShow,
}: PropsWithChildren<TooltipProps>) => {
  const { notifications } = useStore();
  const [rect, setRect] = useState(null);
  const [open, toggle] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const computedStyles = useTooltipComputedStyles({
    rect,
    placement,
    maxWidth,
  });
  const breakpoint = useBreakpoint();
  const gtTablet = ["laptop", "desktop"].includes(breakpoint);

  function onMouseOver() {
    if (!gtTablet) return;
    setRect(wrapper.current.getBoundingClientRect());
    toggle(true);
  }

  function onMouseOut() {
    if (!gtTablet) return;
    toggle(false);
  }

  const onModalOpen = () => {
    if (!title || gtTablet) return;

    notifications.show({
      type: "tooltip",
      component: MobileTooltip,
      props: {
        withoutOKButton,
        children: title,
      },
    });

    onTooltipShow && onTooltipShow();
  };

  return (
    <div
      className={cn([styles.tooltip, open && styles.tooltipOpened])}
      ref={wrapper}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onMouseEnter={onTooltipShow}
      onClick={onModalOpen}
    >
      <>
        {children}
        {open &&
          title &&
          ReactDOM.createPortal(
            <span className={styles.tooltipLabelWrapper} style={computedStyles}>
              <span
                className={cn([styles.tooltipLabel, labelClassName])}
                style={{
                  maxWidth: maxWidth + "px",
                  width: width ? width + "px" : "auto",
                }}
              >
                {title}
              </span>
            </span>,
            document.getElementById("tooltip-portal")
          )}
      </>
    </div>
  );
};

export default Tooltip;
